import { useMemo } from "react";
import { useAuth } from "../auth/AuthProvider";
import { effectiveTimeZone, formatDate, formatDay, formatMonth, formatMonthDay, formatMoney, formatNumber, formatTime, formatWhen, plural, wallTimeToInstant, type WhenOptions } from "./preferences";

type Value = string | number | Date;

export interface Format {
  /** The zone everything is shown in (the chosen one, or the device's). */
  timeZone: string;
  /** Date and time of an instant: `when(e.at)`, `when(x, { seconds: true })`. */
  when: (value: Value, opts?: WhenOptions) => string;
  /** The date alone of an instant, in the user's zone. */
  date: (value: Value) => string;
  /** The time of day alone. */
  time: (value: Value, opts?: WhenOptions) => string;
  /** A calendar date (`YYYY-MM-DD`, or an instant's UTC date) - formatted, never shifted a day by a zone. */
  day: (value: string) => string;
  /** Month and day without the year (chart ticks): `09-14` / `Sep 14`. A bare `YYYY-MM-DD` is a calendar date. */
  monthDay: (value: Value) => string;
  /** A calendar month, `YYYY-MM`: `Sep 2026`. */
  month: (value: string) => string;
  /** A count with grouping (`12,345`). */
  number: (value: number, opts?: Intl.NumberFormatOptions) => string;
  /** US dollars (`$1,234.00`). */
  money: (value: number, opts?: Intl.NumberFormatOptions) => string;
  /** A count and its word: `count(3, "contact", "contacts")` is `3 contacts`, the number grouped, the word by Intl.PluralRules. */
  count: (n: number, one: string, other: string) => string;
  /** The word alone for a count, by Intl.PluralRules: `plural(n, "change", "changes")`. */
  plural: (n: number, one: string, other: string) => string;
  /** A `datetime-local` value read in the user's zone, as the instant it names. */
  instantOf: (local: string) => Date | undefined;
}

/**
 * Section 13.1 and #449: **the one way to render a date, a time or a number.** Honors the user's time zone,
 * date format and time format; storage stays UTC. The lint rule in eslint.config.mjs bans toLocale*String and
 * Intl.DateTimeFormat / NumberFormat outside this module and preferences.ts, so it stays the one way. Stable per
 * preference set, so effects that depend on it do not re-run each render.
 */
export function useFormat(): Format {
  const { preferences } = useAuth();
  const { timeZone, dateFormat, timeFormat } = preferences;
  return useMemo(() => {
    const p = { timeZone, dateFormat, timeFormat };
    const zone = effectiveTimeZone(p);
    return {
      timeZone: zone,
      when: (value, opts) => formatWhen(value, p, opts),
      date: (value) => formatDate(value, p),
      time: (value, opts) => formatTime(value, p, opts),
      day: (value) => formatDay(value, p),
      monthDay: (value) => formatMonthDay(value, p),
      month: (value) => formatMonth(value, p),
      number: (value, opts) => formatNumber(value, p, opts),
      money: (value, opts) => formatMoney(value, p, opts),
      count: (n, one, other) => `${formatNumber(n, p)} ${plural(n, one, other)}`,
      plural,
      instantOf: (local) => wallTimeToInstant(local, zone),
    };
  }, [timeZone, dateFormat, timeFormat]);
}
